import React, { useMemo } from 'react';
import classnames from 'classnames';

import { Button } from 'ui-components/components/button';
import { Modal } from 'ui-components/components/modal';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';
import { GuidService } from 'ui-components/services/guid-service';
import { testId } from 'ui-components/services/testid-service';
import { Text } from 'ui-components/components/text/text';
import { TextType } from 'ui-components/components/text/text-type';

import ModalPortal from '../../components/modal-portal';

import style from './simple-modal.module.less';

/**
 * @typedef {import('ui-components/components/button').ButtonClickHandler} ButtonClickHandler
 */
/**
 * @typedef {import('ui-components/components/text/text-types.d.ts').TextTypeValues} TextTypeValues
 */

/**
 * @callback ModalCloseHandler
 * @property {boolean} [isCancel] - true if the modal was closed by clicking the cancel button
 */

/**
 * @typedef {'custom'|'small'|'medium'|'large'} SimpleModalSize
 * @typedef {import('../../prop-types/intl').MessageDescriptorPropType} MessageDescriptorPropType
 * @typedef {import('../../prop-types/button-theme-service').ButtonThemeServicePropType} ButtonThemeServicePropType
 * @param {object} props
 * @param {React.ReactNode} props.children
 * @param {string} [props.className]
 * @param {string} [props.bodyClassName]
 * @param {SimpleModalSize} [props.size]
 * @param {(event?: React.MouseEvent<HTMLButtonElement>) => void} [props.onCancel]
 * @param {(event?: React.MouseEvent<HTMLButtonElement>)=> void} [props.onSecondAction]
 * @param {ButtonClickHandler} [props.onAction]
 * @param {ButtonThemeServicePropType} [props.actionButtonTheme]
 * @param {MessageDescriptorPropType|string} [props.cancelMessage]
 * @param {MessageDescriptorPropType|string} [props.actionMessage]
 * @param {MessageDescriptorPropType|string} [props.secondActionMessage]
 * @param {MessageDescriptorPropType|string} props.headerMessage
 * @param {ModalCloseHandler} props.onClose
 * @param {boolean} [props.isActionDisabled]
 * @param {MessageDescriptorPropType} [props.confirmCancelBodyMessage]
 * @param {TextTypeValues} [props.headerTextType] default is TextType.H3
 * @param {string} props.actionButtonDataPendoId
 * @param {MessageDescriptorPropType} [props.confirmCancelButtonMessage]
 * @param {MessageDescriptorPropType} [props.confirmCancelHeaderMessage]
 * @param {string} [props.'data-testid']
 * @param {boolean} [props.shouldIgnoreRootFocus]
 * @returns {JSX.Element}
 */
export const SimpleModal = ({
  children,
  onClose,
  size = 'small',
  onCancel,
  onAction,
  onSecondAction,
  headerMessage,
  headerTextType = TextType.H3,
  actionMessage,
  cancelMessage,
  secondActionMessage,
  className,
  bodyClassName = '',
  isActionDisabled = false,
  actionButtonTheme = THEME_CONSTANTS.primary,
  confirmCancelBodyMessage,
  confirmCancelButtonMessage,
  confirmCancelHeaderMessage,
  'data-testid': dataTestId,
  actionButtonDataPendoId = '',
  shouldIgnoreRootFocus = false,
}) => {
  const showFooter = useMemo(
    () => !!(cancelMessage || actionMessage || secondActionMessage),
    [cancelMessage, actionMessage, secondActionMessage],
  );
  const modalHeaderId = useMemo(() => GuidService.newGuid(), []);

  return (
    <ModalPortal
      confirmCancelBodyMessage={confirmCancelBodyMessage}
      confirmCancelButtonMessage={confirmCancelButtonMessage}
      confirmCancelHeaderMessage={confirmCancelHeaderMessage}
      onCancel={onCancel}
      onClose={onClose}
    >
      <Modal className={className} modalHeaderId={modalHeaderId} size={size} {...testId(dataTestId)}>
        <ModalHeader className={style.header} id={modalHeaderId}>
          <Text textType={headerTextType} value={headerMessage} />
        </ModalHeader>
        <ModalBody className={classnames(style.body, bodyClassName)} shouldIgnoreRootFocus={shouldIgnoreRootFocus}>
          {children}
        </ModalBody>
        {showFooter && (
          <ModalFooter className={style.footer}>
            {secondActionMessage && onSecondAction && (
              <Button onClick={onSecondAction} theme={THEME_CONSTANTS.tertiary}>
                <Text textType={TextType.Caption} value={secondActionMessage} />
              </Button>
            )}
            {cancelMessage && onCancel && (
              <Button onClick={onCancel} theme={THEME_CONSTANTS.tertiary}>
                <Text textType={TextType.Caption} value={cancelMessage} />
              </Button>
            )}
            {actionMessage && onAction && (
              <Button data-pendo-id={actionButtonDataPendoId} disabled={isActionDisabled} onClick={onAction} theme={actionButtonTheme}>
                <Text textType={TextType.Caption} value={actionMessage} />
              </Button>
            )}
          </ModalFooter>
        )}
      </Modal>
    </ModalPortal>
  );
};
